<template>
  <div class="relative flex justify-center items-center h-auto min-h-[40vh]">
    <!-- Cercles concentriques animés en arrière-plan -->
    <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div class="absolute size-60 rounded-full animate-pulse-slow" :class="circle200Class" :style="circle200Style">
      </div>
      <div class="absolute size-44 rounded-full animate-pulse-medium" :class="circle300Class" :style="circle300Style">
      </div>
      <div class="absolute size-36 rounded-full animate-pulse-fast" :class="circle400Class" :style="circle400Style">
      </div>
    </div>

    <div class="relative z-10 flex justify-center items-center">
      <!-- Image mode (legacy): used by the existing callers. -->
      <!-- `silhouette` reprend le traitement « carte mystère » du lobby
           (monochrome noir + semi-transparent) pour les items verrouillés. -->
      <img v-if="imageSrc" :src="imageSrc" :alt="imageAlt"
        class="w-full h-auto max-h-[32vh] object-contain drop-shadow-2xl transition-all duration-500"
        :class="silhouette ? 'brightness-0 opacity-60' : { 'blur-sm': blur }" />
      <!-- Slot mode (new): caller provides the central content (icon, number, etc.). -->
      <slot v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  imageSrc?: string;
  imageAlt?: string;
  color?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger';
  blur?: boolean;
  // Traitement silhouette (monochrome noir + semi-transparent), façon carte
  // verrouillée du lobby. Prend le pas sur `blur` quand actif.
  silhouette?: boolean;
  // When set, overrides `color` and the rings adopt these hex values via inline
  // backgroundColor (instead of static Tailwind classes).
  paletteHex?: { hex200: string; hex300: string; hex400: string };
}

const props = withDefaults(defineProps<Props>(), {
  imageAlt: 'Image',
  color: 'primary',
  blur: false,
  silhouette: false,
});

const usePaletteHex = computed(() => !!props.paletteHex);

const circle200Class = computed(() => {
  if (usePaletteHex.value) return '';
  const classes = {
    primary: 'bg-primary-200/20',
    secondary: 'bg-green-200/20',
    tertiary: 'bg-orange-200/20',
    success: 'bg-green-200/20',
    warning: 'bg-yellow-200/20',
    danger: 'bg-red-200/20',
  };
  return classes[props.color];
});

const circle300Class = computed(() => {
  if (usePaletteHex.value) return '';
  const classes = {
    primary: 'bg-primary-300/20',
    secondary: 'bg-green-300/20',
    tertiary: 'bg-orange-300/20',
    success: 'bg-green-300/20',
    warning: 'bg-yellow-300/20',
    danger: 'bg-red-300/20',
  };
  return classes[props.color];
});

const circle400Class = computed(() => {
  if (usePaletteHex.value) return '';
  const classes = {
    primary: 'bg-primary-400/20',
    secondary: 'bg-green-400/20',
    tertiary: 'bg-orange-400/20',
    success: 'bg-green-400/20',
    warning: 'bg-yellow-400/20',
    danger: 'bg-red-400/20',
  };
  return classes[props.color];
});

// 30 % opacity (suffix 4D on 8-digit hex) when palette mode is active.
const circle200Style = computed(() =>
  usePaletteHex.value ? { backgroundColor: `${props.paletteHex!.hex200}4D` } : undefined,
);
const circle300Style = computed(() =>
  usePaletteHex.value ? { backgroundColor: `${props.paletteHex!.hex300}4D` } : undefined,
);
const circle400Style = computed(() =>
  usePaletteHex.value ? { backgroundColor: `${props.paletteHex!.hex400}4D` } : undefined,
);
</script>
